import React, { memo, useCallback, useContext, useState } from 'react';

import { Tooltip, Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { FiLock, FiUnlock } from 'react-icons/fi';

import { useTranslation } from 'react-i18next';
import roles, { RolesTypes } from '@utils/rolesConstants';

import SessionContext from '@providers/Session/SessionContext';
import ActionTypes from '@utils/deviceActions';
import CustomTag from '@components/CustomTag';
import api from '@services/api';
import * as Styled from './styles';

function FinancialBlockStatusColumn({ value, customerId, mutate }: any) {
  const { t } = useTranslation('customers');
  const {
    effectiveRoles,
    theme,
    customer: { customerLogged },
  } = useContext(SessionContext);

  const [loading, setLoading] = useState(false);

  const rolesToAccess = effectiveRoles?.portal;

  const checkRole = useCallback(
    (role: RolesTypes) => rolesToAccess?.includes(role),
    [rolesToAccess],
  );
  const isCustomerCanBlockOrUnlock = checkRole(roles.FINANCIAL_LOCK_UNLOCK);

  const colorFinancialTag = value ? 'red' : 'green';
  const iconFinancialTag = value ? <FiLock /> : <FiUnlock />;
  const tooltipFinancialTag = value
    ? t('actions.financialBlock.blocked')
    : t('actions.financialBlock.unlocked');
  const buttonFinancialBlock = value
    ? t('actions.financialBlock.unlock')
    : t('actions.financialBlock.block');

  const messageContent = useCallback(
    (msg: string) => (
      <>
        {t(`actions.msgs.${msg}`)}
        &nbsp;
      </>
    ),
    [t],
  );

  const handleFinancialBlock = useCallback(
    async (cstId: string, financialBlock: string) => {
      setLoading(true);
      try {
        const payload = {
          action: financialBlock
            ? ActionTypes.FINANCIAL_UNLOCK
            : ActionTypes.FINANCIAL_LOCK,
          customerIds: [cstId],
        };
        const { status } = await api.post(`/service-proxy/actions`, payload);
        mutate();
        // 200 = success | 204 = alreadyInProgress
        const msgType = status === 200 ? 'success' : 'alreadyInProgress';
        message.success({
          key: 'send-action',
          content: messageContent(msgType),
        });
      } catch {
        message.error(t(`actions.msgs.error`));
      }
      setLoading(false);
    },
    [mutate, t, messageContent],
  );

  if (customerId === customerLogged.id) {
    return <></>;
  }

  if (!isCustomerCanBlockOrUnlock) {
    return (
      <CustomTag color={colorFinancialTag}>{tooltipFinancialTag}</CustomTag>
    );
  }

  return (
    <Styled.FinancialBlock>
      {loading ? (
        <LoadingOutlined />
      ) : (
        <>
          <div className="financial-lock-tag-wrapper">
            <Tooltip title={tooltipFinancialTag}>
              <CustomTag
                className="financial-lock-tag"
                color={colorFinancialTag}
              >
                {iconFinancialTag}
              </CustomTag>
            </Tooltip>
            <Button type="link" className="financial-lock-button-arrow">
              &gt;
            </Button>
          </div>
          <Button
            size="small"
            onClick={() => handleFinancialBlock(customerId, value)}
            className="financial-lock-button"
            style={{
              color: value ? theme?.colors?.green : theme?.colors?.red,
            }}
            danger={!value}
          >
            {buttonFinancialBlock}
          </Button>
        </>
      )}
    </Styled.FinancialBlock>
  );
}

export default memo(FinancialBlockStatusColumn);
